/**
 * Calls, meetings and comments on an account, stored as Follow-up tasks on the account
 * property (see ACTIVITY_KINDS in crmConfig.ts for which task field holds what). Tasks are
 * writable with the API key, so logging works on the live API, and zInspector's own Google
 * Calendar sync puts upcoming ones on the calendar.
 */
import type { Identifier } from "ra-core";

import {
  ACTIVITY_DEFAULT_MINUTES,
  ACTIVITY_CANCELED_STATUS,
  ACTIVITY_DONE_STATUS,
  ACTIVITY_KINDS,
  JOURNAL_TASK_PREFIX,
  TASK_TYPE_PK,
  TASK_TYPES,
  type ActivityKind,
} from "./crmConfig";
import type { ZiTask } from "./mappers";

export type ZcrmActivity = {
  id: Identifier;
  company_id: Identifier | null;
  kind: ActivityKind;
  title: string;
  starts_at: string | null;
  ends_at: string | null;
  contact_id: Identifier | null;
  sales_id: Identifier | null;
  notes: string;
  transcript: string;
  source: string;
  is_done: boolean;
  /** A meeting that didn't happen: a no-show (Task.Status Canceled). */
  is_canceled: boolean;
  updated_at: string;
};

/** What the "Log a call" form sends. */
export interface ActivityInput {
  company_id: Identifier;
  kind: ActivityKind;
  title?: string;
  starts_at?: string;
  contact_id?: Identifier | null;
  notes?: string;
  transcript?: string;
  source?: string;
  is_done?: boolean;
}

const MINUTE_MS = 60_000;
const DEFAULT_PRIORITY = 3;
const text = (value: string | null | undefined) => value ?? "";

const isActivityKind = (value: string): value is ActivityKind =>
  (ACTIVITY_KINDS as readonly string[]).includes(value);

/** A Follow-up task that is a call, meeting or comment (the account journal task is not). */
export const isActivityTask = (task: ZiTask): boolean =>
  task.Action === TASK_TYPES.meeting &&
  !text(task.Description).startsWith(JOURNAL_TASK_PREFIX);

export const taskToActivity = (task: ZiTask): ZcrmActivity => {
  const kind = text(task.EntranceInstruction);
  return {
    id: task.id,
    company_id: task.Property ?? null,
    // Follow-ups made in the dashboard carry no kind: they are meetings by default.
    kind: isActivityKind(kind) ? kind : "Meeting",
    title: text(task.Description),
    starts_at: task.StartDate ?? null,
    ends_at: task.EndDate ?? null,
    contact_id: task.AssignedToContact ?? null,
    sales_id: task.ManagedBy ?? null,
    notes: text(task.Notes),
    transcript: text(task.WorkOrderInstructions),
    source: text(task.Key),
    is_done: task.Status === ACTIVITY_DONE_STATUS,
    is_canceled: task.Status === ACTIVITY_CANCELED_STATUS,
    updated_at: text(task.LastModified),
  };
};

const defaultTitle = (kind: ActivityKind) =>
  kind === "Comment" || kind === "To-do" ? kind : `${kind} with the client`;

/**
 * The two writes that log an activity: the create route takes the core task fields, the edit
 * route the rest (kind, notes, transcript, source, status).
 */
export const buildActivityWrites = (
  input: ActivityInput,
  managerId: Identifier | undefined,
  now = new Date(),
): { create: Record<string, unknown>; edit: Record<string, unknown> } => {
  const startsAt = input.starts_at ? new Date(input.starts_at) : now;
  const endsAt = new Date(
    startsAt.getTime() + ACTIVITY_DEFAULT_MINUTES * MINUTE_MS,
  );
  const isDone = input.is_done ?? input.kind === "Comment";
  const owner = managerId == null ? undefined : Number(managerId);
  return {
    create: {
      Action: TASK_TYPE_PK["Follow-up"],
      Priority: DEFAULT_PRIORITY,
      Property: Number(input.company_id),
      Description: input.title?.trim() || defaultTitle(input.kind),
      StartDate: startsAt.toISOString(),
      EndDate: endsAt.toISOString(),
      // Only what is still ahead goes on the calendar.
      IncludeCalendar: !isDone,
      ManagedBy: owner,
      AssignedToUser: owner,
      AssignedToContact: input.contact_id
        ? Number(input.contact_id)
        : undefined,
    },
    edit: {
      EntranceInstruction: input.kind,
      Notes: input.notes?.trim() ?? "",
      WorkOrderInstructions: input.transcript?.trim() ?? "",
      Key: input.source ?? "",
      ...(isDone ? { Status: ACTIVITY_DONE_STATUS } : {}),
    },
  };
};

/** What happened at a meeting: it was held, or they didn't show up. */
export type MeetingOutcome = "held" | "no_show";

/**
 * The edit that turns an activity's changed fields into task fields. A meeting's outcome keeps
 * it on the calendar (held = Closed, no-show = Canceled, which zInspector's sync shows as
 * cancelled); a moved meeting's calendar event moves with it.
 */
export const activityToTaskPatch = (
  data: Partial<ZcrmActivity> & { outcome?: MeetingOutcome },
): Record<string, unknown> => ({
  ...(data.starts_at ? { StartDate: data.starts_at } : {}),
  ...(data.ends_at ? { EndDate: data.ends_at } : {}),
  ...(data.outcome === "held" ? { Status: ACTIVITY_DONE_STATUS } : {}),
  ...(data.outcome === "no_show" ? { Status: ACTIVITY_CANCELED_STATUS } : {}),
  ...(data.title !== undefined ? { Description: data.title } : {}),
  ...(data.notes !== undefined ? { Notes: data.notes } : {}),
  ...(data.transcript !== undefined
    ? { WorkOrderInstructions: data.transcript }
    : {}),
  ...(data.source !== undefined ? { Key: data.source } : {}),
  ...(data.is_done
    ? { Status: ACTIVITY_DONE_STATUS, IncludeCalendar: false }
    : {}),
});
